/**
 * FILE: apps/admin/src/components/ConfirmDialog.tsx
 * PURPOSE: Themed replacement for window.confirm + window.prompt
 *
 *          The browser-native dialogs are jarring on a dark themed app,
 *          can't carry destructive-action affordances, and break Playwright
 *          smoke tests. This file ships two declarative components that
 *          drop into the shared Modal primitive for consistent focus-trap,
 *          scroll-lock, and Esc-close behaviour.
 */

import { useRef, useState } from 'react'
import { Btn } from './ui'
import { Modal } from './Modal'

type ConfirmTone = 'default' | 'danger'

interface ConfirmDialogProps {
  title: string
  body?: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: ConfirmTone
  loading?: boolean
  onConfirm: () => void | Promise<void>
  onCancel: () => void
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  loading = false,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  return (
    <DialogShell title={title} onCancel={onCancel} dismissible={!loading}>
      {body && <p className="text-2xs text-fg-secondary leading-snug">{body}</p>}
      <div ref={wrapperRef} className="flex justify-end gap-1.5 pt-1">
        <Btn variant="cancel" onClick={onCancel} disabled={loading}>
          {cancelLabel}
        </Btn>
        <Btn
          data-primary
          variant={tone === 'danger' ? 'danger' : 'primary'}
          onClick={() => void onConfirm()}
          loading={loading}
        >
          {confirmLabel}
        </Btn>
      </div>
    </DialogShell>
  )
}

interface PromptDialogProps {
  title: string
  body?: string
  label: string
  /** Initial value pre-filled in the input. */
  defaultValue?: string
  placeholder?: string
  confirmLabel?: string
  cancelLabel?: string
  /** Optional input type — defaults to 'text'. Pass 'number' for counts. */
  inputType?: 'text' | 'number'
  /** Validate before submit. Return null when valid, or an error to show. */
  validate?: (value: string) => string | null
  loading?: boolean
  onConfirm: (value: string) => void | Promise<void>
  onCancel: () => void
}

export function PromptDialog({
  title,
  body,
  label,
  defaultValue = '',
  placeholder,
  confirmLabel = 'Save',
  cancelLabel = 'Cancel',
  inputType = 'text',
  validate,
  loading = false,
  onConfirm,
  onCancel,
}: PromptDialogProps) {
  const [value, setValue] = useState(defaultValue)
  const [validationError, setValidationError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return
    const trimmed = value.trim()
    const error = validate ? validate(trimmed) : null
    if (error) {
      setValidationError(error)
      return
    }
    setValidationError(null)
    void onConfirm(trimmed)
  }

  return (
    <DialogShell title={title} onCancel={onCancel} dismissible={!loading}>
      <form onSubmit={handleSubmit} className="space-y-2">
        {body && <p className="text-2xs text-fg-secondary leading-snug">{body}</p>}
        <label className="block text-2xs font-medium text-fg-muted">
          {label}
          <input
            ref={inputRef}
            type={inputType}
            value={value}
            placeholder={placeholder}
            onChange={(e) => {
              setValue(e.currentTarget.value)
              if (validationError) setValidationError(null)
            }}
            disabled={loading}
            className="mt-1 w-full bg-surface-overlay border border-edge-subtle rounded-sm px-2 py-1.5 text-xs text-fg focus:outline-none focus:ring-1 focus:ring-brand/40 disabled:opacity-60"
          />
        </label>
        {validationError && (
          <p role="alert" className="text-2xs text-danger leading-snug">
            {validationError}
          </p>
        )}
        <div className="flex justify-end gap-1.5 pt-1">
          <Btn type="button" variant="cancel" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Btn>
          <Btn type="submit" data-primary loading={loading}>
            {confirmLabel}
          </Btn>
        </div>
      </form>
    </DialogShell>
  )
}

interface DialogShellProps {
  title: string
  children: React.ReactNode
  onCancel: () => void
  dismissible?: boolean
}

function DialogShell({ title, children, onCancel, dismissible = true }: DialogShellProps) {
  return (
    <Modal open onClose={onCancel} title={title} size="sm" dismissible={dismissible}>
      {children}
    </Modal>
  )
}
